import EastIcon from "@mui/icons-material/East";
import { Box, Button, Chip, Stack, TextField, Typography, useTheme } from "@mui/material";
import { useFeatureGate } from "@statsig/react-bindings";
import { shuffle, sortBy, take } from "lodash";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";

import { SendIcon } from "@/Components/Icons/SendIcon";
import { featureFlag } from "@/context/FeatureFlagProvider/featureFlags";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useMetadata } from "@/pages/hendren/hooks/useMetadata";

import { useGrowthCentreStore } from "../store/useGrowthCentreStore";
import { MaiteAlerts } from "./MaiteAlerts";
import { UnlockMaiteModal } from "./UnlockMaiteModal";

const allQuestions = [
  "What are my best and worst performing channels?",
  "Suggest a safe test-and-learn budget split across channels based on current performance.",
  "Which underperforming channels could we reduce to free up budget for testing?",
  "Show YoY change in total revenue and marketing revenue for last quarter",
  "Analyse media mix impact on revenue and ROI over last 2 quarters",
  "Forecast base sales for next quarter based on recent trends",
  "Create executive report on media performance: Last 6 months with YoY comparison",
  "Analyse long-term search performance trends",
  "Identify top performing campaigns from last 12 months",
  "Create a table for the last quarter of media channel revenue and changes YoY",
  "Recommend publisher mix adjustments for top media channels",
  "Forecast Christmas holiday impact on sales",
  "Generate next quarter media plan by optimising last year's performance",
];

// Helper function to randomly select and sort questions by length
const getRandomSortedQuestions = (questions: string[], count: number) => {
  const shuffled = shuffle(questions);
  const selected = take(shuffled, count);
  return sortBy(selected, (item) => item.length);
};

export const MaiteWidget = () => {
  const theme = useTheme();
  const history = useHistory();
  const { buttonClicked } = useAnalytics();
  const {
    isLoading: isMetadataLoading,
    remainingQueries,
    queryLimitReached,
    hendrenTier,
  } = useMetadata();
  const {
    maiteWidget: { question, isModalOpen },
    setMaiteQuestion,
    setMaiteModalOpen,
  } = useGrowthCentreStore();
  const { value: isMaiteV2Enabled } = useFeatureGate(featureFlag.maiteV2Enabled);

  // Dynamic route based on feature flag
  const maiteRoute = isMaiteV2Enabled ? "/maite-v2" : "/maite";

  // Randomly select 3 questions and sort by length (shortest to longest)
  const suggestedQuestions = useMemo(() => {
    return getRandomSortedQuestions(allQuestions, 3);
  }, []);

  const handleQuestionClick = (suggestedQuestion: string) => {
    buttonClicked({
      buttonName: "Suggested Question",
      buttonLocation: "Growth Center - MAITE Widget",
      buttonType: "navigation",
      additionalProperties: {
        question: suggestedQuestion,
        question_length: suggestedQuestion.length,
      },
    });

    // Navigate to Maite with the pre-filled question
    history.push(`${maiteRoute}?question=${encodeURIComponent(suggestedQuestion)}`);
  };

  const handleSubmit = () => {
    if (question.trim()) {
      buttonClicked({
        buttonName: "MAITE Submit",
        buttonLocation: "Growth Center - MAITE Widget",
        buttonType: "submit",
        additionalProperties: {
          question: question.trim(),
          question_length: question.trim().length,
        },
      });

      history.push(`${maiteRoute}?question=${encodeURIComponent(question.trim())}`);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const handleLearnMore = () => {
    buttonClicked({
      buttonName: "Learn More",
      buttonLocation: "Growth Center - MAITE Widget",
      buttonType: "modal",
    });

    setMaiteModalOpen(true);
  };

  const handleCloseModal = () => {
    setMaiteModalOpen(false);
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        mt: 0,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: theme.palette.mode === "light" ? "#0E2244" : "white",
          color: theme.palette.mode === "light" ? "white" : "#0E2244",
          px: 0.5,
          pt: 1,
          pb: 0.5,
          borderRadius: 2,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} mb={1} px={3}>
          <Chip
            label="MAITE"
            size="small"
            sx={{
              backgroundColor: "#e91e63",
              color: "white",
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Growth Answers on Demand
          </Typography>
        </Stack>
        <Box sx={{ p: 2, background: theme.palette.background.paper, borderRadius: 2 }}>
          <Box
            sx={{
              position: "relative",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              marginBottom: 2,
              backgroundColor: queryLimitReached ? "#f5f5f5" : "transparent",
            }}
            p={1}
          >
            <TextField
              multiline
              rows={2}
              value={question}
              onChange={(e) => setMaiteQuestion(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question about your growth data..."
              fullWidth
              variant="standard"
              disabled={queryLimitReached}
              sx={{
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "black !important",
                  color: "black !important",
                },
              }}
              InputProps={{
                disableUnderline: true,
                style: {
                  color: queryLimitReached ? "black" : undefined,
                  WebkitTextFillColor: queryLimitReached ? "black" : undefined,
                },
              }}
            />
            <Stack direction="row" justifyContent="flex-end">
              <Button
                onClick={handleSubmit}
                disabled={!question.trim() || queryLimitReached}
                sx={{
                  opacity: queryLimitReached ? 0.6 : 1,
                }}
              >
                <SendIcon />
              </Button>
            </Stack>
          </Box>

          {/* Alerts */}
          <MaiteAlerts
            queryLimitReached={queryLimitReached}
            remainingQueries={remainingQueries}
            isLoading={isMetadataLoading}
            hendrenTier={hendrenTier}
            onLearnMore={handleLearnMore}
          />

          {/* Suggested Questions */}
          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                mb: 2,
              }}
            >
              What questions can I ask?
            </Typography>
            <Stack spacing={1.5}>
              {suggestedQuestions.map((suggestedQuestion, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <Box>
                    <EastIcon
                      sx={{
                        fontSize: "18px",
                        color: theme.palette.mode === "light" ? "black" : "white",
                      }}
                    />
                  </Box>
                  <Button
                    onClick={() => handleQuestionClick(suggestedQuestion)}
                    disabled={queryLimitReached}
                    sx={{
                      justifyContent: "flex-start",
                      textAlign: "left",
                      backgroundColor: queryLimitReached
                        ? "#f5f5f5"
                        : theme.palette.instantInsights.background,
                      borderRadius: 1,
                      px: 1,
                      textTransform: "none",
                      fontWeight: 500,
                      border: "none",
                      boxShadow: "none",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: theme.palette.action.hover,
                      },
                      "&:active": {
                        backgroundColor: theme.palette.action.selected,
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: queryLimitReached ? "black" : "text.primary" }}
                    >
                      {suggestedQuestion}
                    </Typography>
                  </Button>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>
      </Box>
      <UnlockMaiteModal open={isModalOpen} onClose={handleCloseModal} />
    </Box>
  );
};
